import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import MainLayout from '@/components/MainLayout'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import OnboardingWrapper from '@/components/onboarding/OnboardingWrapper'
import ContextStep from '@/components/steps/ContextStep'
import RoleStep from '@/components/steps/RoleStep'
import ActionStep from '@/components/steps/ActionStep'
import FormatStep from '@/components/steps/FormatStep'
import ToneStep from '@/components/steps/ToneStep'
import ExampleStep from '@/components/steps/ExampleStep'
import { PromptProvider } from '@/contexts/PromptContext'

// Extend window interface for context step ref
declare global {
  interface Window {
    contextStepRef?: {
      generateContextOutput: () => Promise<void>
    }
  }
}

const OnboardingPage: React.FC = () => {
  const { t } = useTranslation('common')
  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 6 // Changed from 7 to 6 since we only have 6 actual steps
  const navigate = useLocalizedNavigate()
  const location = useLocation()
  
  // Get promptId from navigation state
  const promptId = location.state?.promptId

  // Scroll to top when step changes
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      // Find the scrollable container in MainLayout (the flex-1 overflow-y-auto div)
      const mainContent = document.querySelector('.flex-1.overflow-y-auto') as HTMLElement
      if (mainContent) {
        // Use instant scroll on mobile for better performance, smooth on desktop
        const isMobile = window.innerWidth < 640
        mainContent.scrollTo({ 
          top: 0, 
          behavior: isMobile ? 'auto' : 'smooth' 
        })
      } else {
        // Fallback to window scroll
        const isMobile = window.innerWidth < 640
        window.scrollTo({ 
          top: 0, 
          behavior: isMobile ? 'auto' : 'smooth' 
        })
      }
    }, 50) // Reduced delay for faster response

    return () => clearTimeout(timer)
  }, [currentStep])

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    } else {
      // Navigate to refine step when all steps are completed
      navigate('/refine', { state: { promptId } })
    }
  }

  const handleContextNext = () => {
    // Generate context output in the background (non-blocking)
    if (window.contextStepRef && window.contextStepRef.generateContextOutput) {
      window.contextStepRef.generateContextOutput().catch(error => {
        console.error('Background context output generation failed:', error)
      })
    }
    // Proceed immediately without waiting
    handleNext()
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <OnboardingWrapper onNext={handleContextNext} onBack={handleBack} stepType="context">
            <ContextStep />
          </OnboardingWrapper>
        )
      case 2:
        return (
          <OnboardingWrapper onNext={handleNext} onBack={handleBack} stepType="role">
            <RoleStep />
          </OnboardingWrapper>
        )
      case 3:
        return (
          <OnboardingWrapper onNext={handleNext} onBack={handleBack} stepType="action">
            <ActionStep />
          </OnboardingWrapper>
        )
      case 4:
        return (
          <OnboardingWrapper onNext={handleNext} onBack={handleBack} stepType="format">
            <FormatStep />
          </OnboardingWrapper>
        )
      case 5:
        return (
          <OnboardingWrapper onNext={handleNext} onBack={handleBack} stepType="tone">
            <ToneStep />
          </OnboardingWrapper>
        )
      case 6:
        return (
          <OnboardingWrapper onNext={handleNext} onBack={handleBack} stepType="example">
            <ExampleStep />
          </OnboardingWrapper>
        )
      default:
        return (
          <OnboardingWrapper onNext={handleNext} onBack={handleBack} stepType="context">
            <ContextStep />
          </OnboardingWrapper>
        )
    }
  }

  return (
    <PromptProvider promptId={promptId}>
      <MainLayout>
        {/* Progress Bar */}
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-2 sm:py-4">
          <div className="flex justify-center space-x-1 sm:space-x-2 mb-2 overflow-x-auto pb-2">
            {Array.from({ length: totalSteps + 1 }, (_, index) => ( // +1 to include the Refine step
              <div
                key={index}
                className={`h-2 w-8 sm:w-12 md:w-16 rounded flex-shrink-0 ${
                  index < currentStep ? 'bg-gray-800' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <div className="flex justify-center space-x-1 sm:space-x-2 overflow-x-auto">
            <div className="w-8 sm:w-12 md:w-16 text-center text-[8px] sm:text-[9px] md:text-xs text-gray-500 flex-shrink-0">
              <span className={currentStep >= 1 ? 'text-gray-800 font-medium' : ''}>{t('progress.context')}</span>
            </div>
            <div className="w-8 sm:w-12 md:w-16 text-center text-[8px] sm:text-[9px] md:text-xs text-gray-500 flex-shrink-0">
              <span className={currentStep >= 2 ? 'text-gray-800 font-medium' : ''}>{t('progress.role')}</span>
            </div>
            <div className="w-8 sm:w-12 md:w-16 text-center text-[8px] sm:text-[9px] md:text-xs text-gray-500 flex-shrink-0">
              <span className={currentStep >= 3 ? 'text-gray-800 font-medium' : ''}>{t('progress.action')}</span>
            </div>
            <div className="w-8 sm:w-12 md:w-16 text-center text-[8px] sm:text-[9px] md:text-xs text-gray-500 flex-shrink-0">
              <span className={currentStep >= 4 ? 'text-gray-800 font-medium' : ''}>{t('progress.format')}</span>
            </div>
            <div className="w-8 sm:w-12 md:w-16 text-center text-[8px] sm:text-[9px] md:text-xs text-gray-500 flex-shrink-0">
              <span className={currentStep >= 5 ? 'text-gray-800 font-medium' : ''}>{t('progress.tone')}</span>
            </div>
            <div className="w-8 sm:w-12 md:w-16 text-center text-[8px] sm:text-[9px] md:text-xs text-gray-500 flex-shrink-0">
              <span className={currentStep >= 6 ? 'text-gray-800 font-medium' : ''}>{t('progress.example')}</span>
            </div>
            <div className="w-8 sm:w-12 md:w-16 text-center text-[8px] sm:text-[9px] md:text-xs text-gray-500 flex-shrink-0">
              <span className={currentStep >= 7 ? 'text-gray-800 font-medium' : ''}>{t('progress.refine')}</span>
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          {renderStep()}
        </div>
      </MainLayout>
    </PromptProvider>
  )
}

export default OnboardingPage